import { type PropsWithChildren, useEffect, useRef } from 'react';

import { config } from '~app/config';

import type { AuthApi } from '~entities/auth';
import { AUTH_API_KEY } from '~entities/auth';
import {
  isAuthorizedAtom,
  lastInteractionTimeAtom,
  resetSessionAtom,
} from '~entities/auth';

import { useApi } from '~shared/providers/use-api';

import { useAtomValue, useSetAtom } from 'jotai';

import { useUserInteractionTracking, countRefreshInterval } from '../lib';

const inactiveThreshold = config.inactivityThreshold * 60 * 1000;

export const ActivityGuard = ({ children }: PropsWithChildren) => {
  const authApi = useApi<AuthApi>(AUTH_API_KEY);
  const lastInteraction = useRef(Date.now());
  const lastScheduledHandler = useRef<NodeJS.Timeout | null>(null);

  const isAuthorized = useAtomValue(isAuthorizedAtom);
  const resetSesseion = useSetAtom(resetSessionAtom);
  const lastInteractionTime = useAtomValue(lastInteractionTimeAtom);

  useUserInteractionTracking();

  useEffect(() => {
    if (lastInteractionTime) {
      lastInteraction.current = lastInteractionTime;
    }
  }, [lastInteractionTime]);

  useEffect(() => {
    const initiateSessionUpdates = () => {
      if (Date.now() - lastInteraction.current < inactiveThreshold) {
        authApi
          .validate()
          .then((resp) => {
            const refreshInterval = countRefreshInterval(
              resp.refreshRemainingMinutes,
              resp.expRemainingMinutes
            );

            if (lastScheduledHandler.current !== null) {
              clearTimeout(lastScheduledHandler.current);
            }

            lastScheduledHandler.current = setTimeout(
              initiateSessionUpdates,
              refreshInterval
            );
          })
          .catch(() => {
            resetSesseion();
          });
      } else {
        resetSesseion();
        authApi.revoke();
      }
    };

    if (isAuthorized) {
      initiateSessionUpdates();
    }
    return () => {
      if (lastScheduledHandler.current) {
        clearTimeout(lastScheduledHandler.current);
      }
    };
  }, [isAuthorized, resetSesseion, authApi]);

  return <>{children}</>;
};
